/* Uses the original form: one submit handler, no parallel cart/add request. */
if (!customElements.get('meridiana-buy-bar')) {
  customElements.define('meridiana-buy-bar', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.hidden = true;
      this.hasScrolledDown = false;
      this.pastButton = false;
      this.lastScroll = window.scrollY;
      this.locked = false;
      this.abort = new AbortController();
      this.button = this.querySelector('[data-buybar-submit]');
      this.label = this.querySelector('[data-buybar-label]');
      this.price = this.querySelector('[data-buybar-price]');
      this.availability = this.querySelector('[data-buybar-availability]');
      this.variantLabel = this.querySelector('[data-buybar-variant]');
      this.queue = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      this.button.addEventListener('click', () => this.submitOriginal(), {signal:this.abort.signal});
      window.addEventListener('scroll', () => {
        if (window.scrollY > this.lastScroll) this.hasScrolledDown = true;
        this.lastScroll = window.scrollY;
        this.queue();
      }, {passive:true, signal:this.abort.signal});
      window.addEventListener('resize', this.queue, {passive:true, signal:this.abort.signal});
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.hasScrolledDown = false;
        this.lastScroll = window.scrollY;
        this.setVisible(false);
        this.queue();
      }, {signal:this.abort.signal});
      document.addEventListener('change', this.queue, {capture:true, signal:this.abort.signal});
      document.addEventListener('input', this.queue, {capture:true, signal:this.abort.signal});
      this.bodyObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queue();
      });
      this.bodyObserver.observe(document.body, {subtree:true, childList:true, attributes:true, attributeFilter:['class','style','hidden','open','aria-hidden','aria-disabled','disabled']});
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(this.queue);
        this.sizeObserver.observe(this);
      }
      this.bindSource();
      this.sync();
    }

    bindSource() {
      const scope = document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('main .product');
      const primary = scope?.querySelector('button[id^="ProductSubmitButton-"]');
      if (primary === this.primary && scope === this.scope) return;
      this.intersection?.disconnect();
      this.sourceObserver?.disconnect();
      this.scope = scope;
      this.primary = primary;
      this.form = primary?.form;
      this.pastButton = false;
      if (!primary || !this.form) return;
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= (entry.rootBounds?.top || 0);
          this.queue();
        }, {root:null, threshold:0});
        this.intersection.observe(primary);
      }
      this.sourceObserver = new MutationObserver(this.queue);
      this.sourceObserver.observe(scope, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['disabled','aria-disabled','class','value','src','srcset']});
    }

    isVisibleElement(element) {
      if (!element || element.hidden) return false;
      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    }

    hasOverlay() {
      const selector = 'cart-drawer.active,cart-notification.active,.drawer.active,header-drawer details[open],details-modal details[open],dialog[open],[role="dialog"][aria-modal="true"],[role="alertdialog"],#iubenda-iframe.iubenda-iframe-visible,#iubenda-cs-banner,#shopify-pc__banner,.shopify-pc__banner';
      return Array.from(document.querySelectorAll(selector)).some(element => !this.contains(element) && this.isVisibleElement(element));
    }

    sync() {
      this.bindSource();
      if (!this.primary || !this.form || !this.scope) { this.setVisible(false); return; }
      const loading = this.primary.classList.contains('loading') || this.primary.getAttribute('aria-disabled') === 'true';
      if (this.locked && !loading) this.locked = false;
      const variantInput = this.form.querySelector('[name="id"]');
      const hasVariant = Boolean(variantInput?.value && !variantInput.disabled);
      const unavailable = this.primary.disabled || !hasVariant;
      const blocked = unavailable || loading || this.locked;
      this.button.disabled = blocked;
      this.button.setAttribute('aria-busy', loading || this.locked ? 'true' : 'false');
      const originalText = this.primary.querySelector('span')?.textContent.trim() || this.primary.textContent.trim();
      this.label.textContent = loading || this.locked ? 'Aggiunta in corso…' : unavailable ? originalText : 'Acquista subito';
      const priceSource = this.scope.querySelector('.price--on-sale .price-item--sale') || this.scope.querySelector('.price__regular .price-item--regular');
      if (!hasVariant) this.price.textContent = '—';
      else if (priceSource) this.price.textContent = priceSource.textContent.trim();
      else {
        const priceContainer = this.scope.querySelector('[id^="price-"]');
        if (priceContainer && !priceContainer.classList.contains('hidden')) this.price.textContent = priceContainer.textContent.trim();
      }
      const inventory = this.scope.querySelector('[id^="Inventory-"]');
      this.availability.textContent = loading ? 'Aggiornamento…' : unavailable ? originalText : inventory?.textContent.trim() || 'Disponibile';
      this.availability.classList.toggle('meridiana-buy-bar__availability--unavailable', unavailable);
      const quantity = this.form.elements.namedItem('quantity');
      let selectedVariant;
      try { selectedVariant = JSON.parse(this.scope.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null'); } catch { selectedVariant = null; }
      const details = [];
      if (selectedVariant?.title && selectedVariant.title !== 'Default Title') details.push(selectedVariant.title);
      if (quantity && quantity.value) details.push(`Quantità: ${quantity.value}`);
      this.variantLabel.textContent = details.join(' · ');
      const image = this.querySelector('.meridiana-buy-bar__image');
      const sourceImage = this.scope.querySelector('media-gallery .product__media-list .is-active img') || this.scope.querySelector('media-gallery .product__media-list img');
      if (image && sourceImage) {
        const source = sourceImage.currentSrc || sourceImage.src;
        if (source && image.getAttribute('src') !== source) image.src = source;
      }
      // The element must actually have passed above the viewport. Initial position below it never activates the bar.
      if (!this.intersection) this.pastButton = this.primary.getBoundingClientRect().bottom <= 0;
      this.setVisible(this.hasScrolledDown && this.pastButton && !this.hasOverlay());
      if (!this.hidden) {
        const height = `${Math.ceil(this.getBoundingClientRect().height)}px`;
        if (document.documentElement.style.getPropertyValue('--meridiana-buybar-height') !== height) document.documentElement.style.setProperty('--meridiana-buybar-height', height);
      }
    }

    setVisible(visible) {
      if (this.hidden === !visible) return;
      this.hidden = !visible;
      document.body.classList.toggle('meridiana-buybar-visible', visible);
    }

    submitOriginal() {
      if (this.locked) return;
      this.sync();
      if (this.button.disabled || !this.form || !this.primary) return;
      if (!this.form.reportValidity()) return;
      this.locked = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      // requestSubmit uses the existing native/Dawn handler, including selected options,
      // quantity, line item properties, gift-card fields, stock errors and cart drawer.
      this.form.requestSubmit(this.primary);
      this.queue();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.intersection?.disconnect();
      this.sourceObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.sizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      document.body.classList.remove('meridiana-buybar-visible');
      this.initialized = false;
    }
  });
}
